import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import Duration from "./images/duration.png";

import "./Styling.css";

const MetricsGrid = ({ insights }) => {
  const [showIcon, setShowIcon] = useState(false);

  const [flippedIndices, setFlippedIndices] = useState([]);

  // Enrichment tokens and cost details
  const inputTokenCost = 0.00000025;
  const outputTokenCost = 0.00000125;

  const inputTokensDuration = insights?.tokensInDuration?.sum_input_tokens;
  const outputTokensDuration = insights?.tokensInDuration?.sum_output_tokens;

  const totalInputTokens = insights?.tokenCount?.sum_input_tokens;
  const totalOutputTokens = insights?.tokenCount?.sum_output_tokens;

  const enrichmentCost =
    inputTokensDuration * inputTokenCost +
    outputTokensDuration * outputTokenCost;
  const totalEnrichmentCost =
    totalInputTokens * inputTokenCost + totalOutputTokens * outputTokenCost;

  // Query token enrichment and cost details
  const inputQueryTokenCost = 0.000003;
  const outputQueryTokenCost = 0.000015;

  const inputQueryTokensDuration =
    insights?.insightsInDuration?.query_tokens?.input_tokens;

  const outputQueryTokensDuration =
    insights?.insightsInDuration?.query_tokens?.output_tokens;
  const totalInputQueryTokens =
    insights?.totalInsights?.query_tokens?.input_tokens;
  const totalOutputQueryTokens =
    insights?.totalInsights?.query_tokens?.output_tokens;

  const queryEnrichmentCost =
    inputQueryTokensDuration * inputQueryTokenCost +
    outputQueryTokensDuration * outputQueryTokenCost;

  const totalQueryEnrichmentCost =
    totalInputQueryTokens * inputQueryTokenCost +
    totalOutputQueryTokens * outputQueryTokenCost;

  const handleFlip = (index) => {
    setFlippedIndices((prevState) =>
      prevState.includes(index)
        ? prevState.filter((id) => id !== index)
        : [...prevState, index]
    );
  };

  const formatCurrency = (amount) => {
    if (typeof amount !== "number") return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num, compact = false) => {
    if (typeof num !== "number" || isNaN(num)) return "N/A";

    if (!compact) return num.toLocaleString();

    const absNum = Math.abs(num); // To handle negative numbers correctly
    if (absNum >= 1_000_000_000_000) {
      return (num / 1_000_000_000_000).toFixed(3) + "T";
    }
    if (absNum >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(3) + "B";
    }
    if (absNum >= 1_000_000) {
      return (num / 1_000_000).toFixed(3) + "M";
    }
    if (absNum >= 1_000) {
      return (num / 1_000).toFixed(3) + "k";
    }

    return num.toString();
  };

  const formatTooltipContent = (data) => {
    if (!data) return "No extra info";
    return Object.entries(data)
      .filter(([key]) => key !== "monthly_breakdown")
      .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
      .join("<br />");
  };

  const metrics = [
    {
      title: `Data Collected`,
      flipTitle: "Total Data Collected",
      value: formatNumber(
        insights?.dataCollection?.totalMentionsCountThisMonth,
        true
      ),
      flipValue: formatNumber(
        insights?.dataCollection?.totalMentionsFromBeginning,
        true
      ),
      unit: "entries",
      icon: "🗄️",
      bg: "#E0EDFF",
    },
    {
      title: `Enrichment Tokens`,
      flipTitle: "Total Enrichment Tokens",
      value: formatNumber(insights?.tokensInDuration?.sum_total_tokens, true),
      flipValue: formatNumber(insights?.tokenCount?.sum_total_tokens, true),
      unit: "tokens",
      icon: "📄",
      bg: "#D7FBE4",
    },
    {
      title: `Enrichment cost`,
      flipTitle: "Total Enrichment Cost",
      value: `$${enrichmentCost.toFixed(4)}`,
      flipValue: `$${totalEnrichmentCost.toFixed(4)}`,
      unit: "",
      icon: "🔗",
      bg: "#FFF2CC",
    },
    {
      title: `New Signups`,
      flipTitle: "Total Signups",
      value:
        insights.usersInDuration.total_categorized.external_users +
        insights.usersInDuration.total_categorized.external_loco_login_users,
      flipValue:
        insights.summary.total_categorized.external_users +
        insights.summary.total_categorized.external_loco_login_users,
      unit: "users",
      icon: "👥",
      bg: "#F3E8FF",
      tooltipFront: insights?.usersInDuration?.total_categorized,
      tooltipBack: insights?.summary?.total_categorized,
    },
    {
      title: `Questions Asked`,
      flipTitle: "Total Questions Asked",
      value: formatNumber(insights?.insightsInDuration.total_questions),
      flipValue: formatNumber(insights?.totalInsights.total_questions),
      unit: "Questions",
      icon: "❓",
      bg: "#EAF4FF",
    },
    {
      title: `Execution Stats`,
      flipTitle: "Total Execution Stats",
      value: `${(
        insights?.insightsInDuration.execution_time_stats.average_time_ms / 1000
      ).toFixed(2)} s`,
      flipValue: `${(
        insights?.totalInsights.execution_time_stats.average_time_ms / 1000
      ).toFixed(2)} s`,
      unit: "seconds",
      icon: "⏱️",
      bg: "#FFE5E0",
      tooltipFront: insights?.insightsInDuration.execution_time_stats,
      tooltipBack: insights?.totalInsights.execution_time_stats,
    },
    {
      title: `Query Tokens`,
      flipTitle: "Total Query Tokens",
      value: formatNumber(
        insights?.insightsInDuration?.query_tokens?.total_tokens,
        true
      ),
      flipValue: formatNumber(
        insights?.totalInsights?.query_tokens?.total_tokens,
        true
      ),
      unit: "tokens",
      icon: "📄",
      bg: "#E0EDFF",
    },
    {
      title: `Query Enrichment cost`,
      flipTitle: "Total Query Enrichment Cost",
      value: `$${queryEnrichmentCost.toFixed(4)}`,
      flipValue: `$${totalQueryEnrichmentCost.toFixed(4)}`,
      unit: "",
      icon: "🔗",
      bg: "#D7FBE4",
    },
  ];
  useEffect(() => {
    const storedStart = localStorage.getItem("startDate");
    const storedEnd = localStorage.getItem("endDate");

    if (!storedStart || !storedEnd) {
      setShowIcon(false);
      return;
    }

    const startDate = new Date(storedStart);
    const endDate = new Date(storedEnd);

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const isDefaultStart =
      startDate.getFullYear() === firstDayOfMonth.getFullYear() &&
      startDate.getMonth() === firstDayOfMonth.getMonth() &&
      startDate.getDate() === firstDayOfMonth.getDate();

    const isDefaultEnd =
      endDate.getFullYear() === today.getFullYear() &&
      endDate.getMonth() === today.getMonth() &&
      endDate.getDate() === today.getDate();

    setShowIcon(!(isDefaultStart && isDefaultEnd));
  }, []);

  if (!metrics.length)
    return <div className="no-data">No metrics data available</div>;

  return (
    <div className="metrics-grid">
      {metrics.map((item, index) => (
        <div
          className="metric-card"
          key={index}
          onClick={() => handleFlip(index)}
          style={{
            backgroundColor: flippedIndices.includes(index)
              ? "#f0f0f0"
              : "#ffffff",
          }}
        >
          {showIcon && (
            <img src={Duration} alt="duration" className="duration-icon" />
          )}
          <div
            className={`card-inner ${
              flippedIndices.includes(index) ? "flipped" : ""
            }`}
          >
            <div className="card-front">
              <div className="metric-content">
                <div>
                  <p className="metric-title">{item.title}</p>
                  <h2
                    className="metric-value"
                    data-tooltip-id={`tooltip-${index}`}
                    data-tooltip-html={
                      item.tooltipFront
                        ? formatTooltipContent(item.tooltipFront)
                        : undefined
                    }
                  >
                    {item.value}
                  </h2>
                  <p className="metric-unit">{item.unit}</p>
                </div>
                <div
                  className="metric-icon"
                  style={{ backgroundColor: item.bg }}
                >
                  {item.icon}
                </div>
              </div>
            </div>
            <div className="card-back">
              <div className="metric-content">
                <div>
                  <p className="metric-title">{item.flipTitle}</p>
                  <h2
                    className="metric-value"
                    data-tooltip-id={`tooltip-back-${index}`}
                    data-tooltip-html={
                      item.tooltipBack
                        ? formatTooltipContent(item.tooltipBack)
                        : undefined
                    }
                  >
                    {item.flipValue}
                  </h2>
                  <p className="metric-unit">{item.unit}</p>
                </div>
                <div
                  className="metric-icon"
                  style={{ backgroundColor: item.bg }}
                >
                  {item.icon}
                </div>
              </div>
            </div>
          </div>

          {/* Tooltips for front and back */}
          <Tooltip id={`tooltip-${index}`} place="top" />
          <Tooltip id={`tooltip-back-${index}`} place="top" />
        </div>
      ))}
    </div>
  );
};

export default MetricsGrid;
